import React, { useState, useEffect, useMemo } from 'react'
import { Button, Drawer, Tooltip } from 'antd'
import {
  mapStateToProps,
  mapDispatchToProps,
  connect
} from '../../../reducers/Dispatchers'
import { request, generalErrorHandler, isNonEmptyArray } from '../../../utils'
import SmartTable, { SmartTableDataTypes } from '../../shared/SmartTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons'
import UsersAssignModal from './UsersAssignModal'
import { Contexts, SupportedRoles } from '../../../utils/constants'
import { HttpMethods } from '../../../utils/apiHelper'
import { isInstitutionUser } from '../../../utils/roles'

const SectorSectionUsers = props => {
  const [title, setTitle] = useState('')
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const roleNames = useMemo(() => [
    {
      name: props.t('common.coordinator'),
      apiName: SupportedRoles.COORDINATOR
    },
    { name: props.t('Administrator'), apiName: SupportedRoles.ENCODER },
    { name: props.t('Supervisor'), apiName: SupportedRoles.SUPERVISOR },
    {
      name: props.t('Validator'),
      apiName: SupportedRoles.VALIDATOR
    },
    {
      name: props.t('Administrator'),
      apiName: SupportedRoles.HOSPITAL_ADMIN
    },
    {
      name: props.t('Observer'),
      apiName: SupportedRoles.OBSERVER
    },
    {
      name: props.t('Nurse'),
      apiName: SupportedRoles.NURSE
    },
    {
      name: props.t('Admin'),
      apiName: SupportedRoles.ADMIN
    }
  ], [props.t])
  const columns = useMemo(() => [
    { type: SmartTableDataTypes.ID, key: 'id' },
    {
      type: SmartTableDataTypes.STRING,
      name: props.t('Lastname'),
      key: 'lastname'
    },
    {
      type: SmartTableDataTypes.STRING,
      name: props.t('Firstname'),
      key: 'firstname'
    },
    {
      type: SmartTableDataTypes.STRING,
      name: props.t('Email'),
      key: 'email'
    },
    {
      type: SmartTableDataTypes.STRING,
      name: props.t('Role'),
      key: 'role'
    }
  ], [props.t])

  useEffect(() => {
    if (props.entity) {
      setLoading(true)

      setTitle(props.getUser.context === Contexts.INSTITUTION ? props.t('Care unit users') : props.t('Section users'))
      getUsers()
    }
  }, [props.entity])

  const extractRoleName = roles => {
    const rawRole = roles[0]
    const foundRoleName = roleNames.find(item => item.apiName === rawRole)
    return foundRoleName ? foundRoleName.name : props.t('None')
  }

  const getUsers = async () => {
    const requestUrl = props.getUser.context === Contexts.INSTITUTION
      ? `/api/Sector/${props.entity.id}/all`
      : `/api/school-sections/${props.entity.id}?coordinators=true`

    try {
      const { data } = await request(requestUrl, HttpMethods.GET, null, props.getUser)

      if (data) {
        const usersToShow = []

        if (isInstitutionUser(props.getUser)) {
          if (isNonEmptyArray(data.affiliates)) {
            data.affiliates.forEach(user => {
              if (!usersToShow.find(existingUser => existingUser.id === user.id)) {
                usersToShow.push({ ...user, role: extractRoleName(user.roles) })
              }
            })
          }

          if (isNonEmptyArray(data.supervisedBy)) {
            data.supervisedBy.forEach(user => {
              if (!usersToShow.find(existingUser => existingUser.id === user.id)) {
                usersToShow.push({ ...user, role: extractRoleName(user.roles) })
              }
            })
          }

          if (isNonEmptyArray(data.tutors)) {
            data.tutors.forEach(user => {
              if (!usersToShow.find(existingUser => existingUser.id === user.id)) {
                usersToShow.push({ ...user, role: extractRoleName(user.roles) })
              }
            })
          }
        } else {
          if (isNonEmptyArray(data.users)) {
            data.users.forEach(user => {
              if (!usersToShow.find(existingUser => existingUser.id === user.id)) {
                usersToShow.push({ ...user, role: extractRoleName(user.roles) })
              }
            })
          }
        }

        setUsers(usersToShow)
      }

      setLoading(false)
    } catch (err) {
      generalErrorHandler(err)
      setLoading(false)
    }
  }

  const handleDrawerClose = () => {
    if (typeof props.onClose === 'function') {
      setUsers([])
      props.onClose()
    }
  }

  const secondaryButtons = useMemo(() => {
    const secondaryButtons = []

    if (props.getUser.context === Contexts.INSTITUTION && !props.disableAddButton) {
      secondaryButtons.push(
        <Button key='assign-users-button' onClick={() => setIsVisible(!isVisible)}>
          <Tooltip placement='right' title='Assign users'>
            <FontAwesomeIcon icon={faUserPlus} />
          </Tooltip>
        </Button>
      )
    }

    return secondaryButtons
  }, [setIsVisible, props.t, isVisible, props.getUser.context])

  return (
    <Drawer
      title={title}
      width='50%'
      onClose={handleDrawerClose}
      visible={props.entity !== null}
    >
      <SmartTable
        secondaryButtons={secondaryButtons}
        columns={columns}
        data={users}
        loading={loading}
      />
      {isVisible && (
        <UsersAssignModal sector={props.entity} visible={isVisible} onClose={() => setIsVisible(!isVisible)} onAssign={getUsers} careUnitUsers={users} />
      )}
    </Drawer>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(SectorSectionUsers)
